import { Component, OnInit, ViewChild, ElementRef, AfterViewInit, Renderer2, OnDestroy } from '@angular/core';
import { AnimationController, ToastController, IonContent } from '@ionic/angular';
import { IonSegmentCustomEvent, SegmentChangeEventDetail } from '@ionic/core';
import { LoginService } from '../services/login.service';
import { TranslateService } from '@ngx-translate/core';
import { jwtDecode } from 'jwt-decode';
import { MenuController } from '@ionic/angular';
import { LanguageService } from '../services/language.service';
import { GameStateManagerService } from '../services/game-state-manager.service';
import { GameControlService } from '../end-game/game-service';
import { DeveloperAuthService } from '../services/developer-auth.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-home',
  templateUrl: 'home.page.html',
  styleUrls: ['home.page.scss'],
})
export class HomePage implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('featuresGrid', { static: true }) featuresGrid!: ElementRef;
  @ViewChild(IonContent, { static: false }) content!: IonContent;
  @ViewChild('logoContainer', { static: false }) logoContainer!: ElementRef;
  @ViewChild('homeSection', { static: false }) homeSection!: ElementRef;

  activeSection: 'participants' | 'organisers' = 'participants';
  benefitsVisible = false;

  // Add language subscription
  private languageSubscription!: Subscription;

  // Developer password protection
  isPasswordVerified: boolean = false;

  constructor(
    private animationCtrl: AnimationController,
    private loginService: LoginService,
    private translate: TranslateService,
    private toastController: ToastController,
    private menuController: MenuController,
    private renderer: Renderer2,
    private languageService: LanguageService,
    private gameStateManager: GameStateManagerService,
    private gameControlService: GameControlService,
    private developerAuth: DeveloperAuthService
  ) {}

  isLoggedIn: boolean = false;
  showProfileDropdown = false;

  firstName: string | null = null;
  lastName: string | null = null;
  mykadNumber: string | null = null;
  email: string | null = null;
  school: string | null = null;
  state: string | null = null;

  isSegmentVisible = false;
  private scrollThreshold = 300; // Adjust this value as needed

  window = window;

  contestStatus: 'upcoming' | 'live' | 'ended' = 'upcoming';
  timeUntilStart: string = '';

  // Competition time constants
  private readonly COMPETITION_DATE = '2024-12-07';
  private readonly START_TIME = '09:00:00';
  private readonly END_TIME = '21:00:00';
  private readonly TIMEZONE_OFFSET = '+08:00'; // MYT timezone offset

  selectedLanguage: string = 'en';

  async ngOnInit() {
    // Check for developer password first
    await this.checkDeveloperAccess();
    
    this.activeSection = 'participants'; // Set the initial section to 'participants'

    // Initialize language from service
    this.selectedLanguage = this.languageService.getCurrentLanguage();
    this.translate.use(this.selectedLanguage);

    // Subscribe to language changes
    this.languageSubscription = this.languageService.language$.subscribe((lang: string) => {
      // Always update both the selected language and translation service
      this.selectedLanguage = lang;
      this.translate.use(lang);
    });

    const token = await this.loginService.getToken();

    if (token) {
      const decodedToken: any = jwtDecode(token);
  
      this.mykadNumber = decodedToken?.data?.mykad;
      const userDetails = await this.loginService.getUserDetails();
  
      if (userDetails) {
        this.firstName = userDetails.firstName;
        this.lastName = userDetails.lastName;
        this.email = userDetails.email;
        this.state = userDetails.state;
      }
      this.isLoggedIn = true;
    }

    // Initial update
    this.updateContestStatus();
    
    // Update every minute, using setInterval with arrow function
    const timer = setInterval(() => {
      this.updateContestStatus();
    }, 60000);

    // Clean up timer on component destroy
    this.ngOnDestroy = () => {
      clearInterval(timer);
      if (this.languageSubscription) {
        this.languageSubscription.unsubscribe();
      }
    };
  }

  private async checkDeveloperAccess() {
    this.isPasswordVerified = await this.developerAuth.checkDeveloperAccess();
  }

  ngOnDestroy() {
    // Will be overwritten by ngOnInit if needed
    if (this.languageSubscription) {
      this.languageSubscription.unsubscribe();
    }
  }

  ionViewWillEnter() {
    // Ensure language is synchronized when entering the page
    const currentLang = this.languageService.getCurrentLanguage();
    if (this.selectedLanguage !== currentLang) {
      this.selectedLanguage = currentLang;
      this.translate.use(currentLang);
    }
  }

  ionViewDidEnter() {
    // Clear any lingering game states when returning to home
    this.clearAllGameStates();
  }

  private clearAllGameStates() {
    // Clear all game states from our state manager
    this.gameStateManager.clearAllGameStates();
    this.gameControlService.forceCleanup();

    // Clear any game states that might be persisting in localStorage
    if (typeof window !== 'undefined' && window.localStorage) {
      // Clear common game storage keys
      const gameKeys = [
        'SearchWordProgress',
        'jumbleword_progress',
        'groupword_progress',
        'audioword_progress',
        'matchword_progress',
        'createword_progress',
        'blankword_progress',
        'errorword_progress',
        'guessword_progress',
        'pairsgame_progress',
        'mark_progress',
        'stackword_progress',
        'pictureword_progress',
        'changeword_progress',
        'reformword_progress',
        'crossword_progress'
      ];

      gameKeys.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (error) {
          console.warn(`Failed to clear ${key}:`, error);
        }
      });
    }
  }

  private updateContestStatus() {
    // Get current time in MYT
    const now = this.getMYTDate();
    const competitionDate = this.COMPETITION_DATE;
    
    // Create dates with MYT timezone
    const startTime = new Date(`${competitionDate}T${this.START_TIME}${this.TIMEZONE_OFFSET}`);
    const endTime = new Date(`${competitionDate}T${this.END_TIME}${this.TIMEZONE_OFFSET}`);

    if (now < startTime) {
      this.contestStatus = 'upcoming';
      this.updateTimeUntilStart(startTime, now);
    } else if (now >= startTime && now <= endTime) {
      this.contestStatus = 'live';
    } else {
      this.contestStatus = 'ended';
    }
  }

  private getMYTDate(): Date {
    // Create a date object for the current time
    const now = new Date();
    
    // Convert to MYT by adding 8 hours to UTC
    const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
    return new Date(utc + (3600000 * 8));
  }

  private updateTimeUntilStart(startTime: Date, now: Date) {
    const diff = startTime.getTime() - now.getTime();
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) {
      this.timeUntilStart = `${days}d ${hours}h`;
    } else if (hours > 0) {
      this.timeUntilStart = `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      this.timeUntilStart = `${minutes}m`;
    } else {
      this.timeUntilStart = 'Starting soon';
    }
  }

  // Toggle profile dropdown visibility
  toggleProfileDropdown() {
    this.showProfileDropdown = !this.showProfileDropdown;
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.content.ionScroll.subscribe(($event: CustomEvent) => {
        if (this.homeSection && this.homeSection.nativeElement) {
          const scrollTop = $event.detail.scrollTop;
          const logoContainer = this.homeSection.nativeElement.querySelector('.logo-container');
          const headerLogo = document.querySelector('.header-logo') as HTMLElement;
          
          if (logoContainer && headerLogo) {
            const logoRect = logoContainer.getBoundingClientRect();

            // Show header logo when the content logo is out of view
            if (logoRect.bottom < 0) {
              if (!headerLogo.classList.contains('show-logo')) {
                headerLogo.classList.add('show-logo');
              }
            } else {
              if (headerLogo.classList.contains('show-logo')) {
                headerLogo.classList.remove('show-logo');
              }
            }
          }

          // Keep the segment visibility logic
          if (scrollTop > this.scrollThreshold) {
            this.isSegmentVisible = true;
          } else {
            this.isSegmentVisible = false;
          }
        }
      });
    }, 100);
  }

  async signOut() {
    await this.loginService.signOut();
    this.isLoggedIn = false;
    this.firstName = null;
    this.lastName = null;
    this.mykadNumber = null;
    this.school = null;
    this.state = null;
    this.loginService.setAuthState(false);

    const ToastAnchor = this.translate.instant('Logout successful!');
    this.presentToast(ToastAnchor);
  }

  async presentToast(message: string) {
    const toast = await this.toastController.create({
      message,
      duration: 2000,
      position: 'bottom'
    });
    await toast.present();
  }

  toggleSection(event: Event) {
    const customEvent = event as IonSegmentCustomEvent<SegmentChangeEventDetail>;
    this.activeSection = customEvent.detail.value as 'participants' | 'organisers';
  }

  openAccountMenu() {
    this.menuController.open('end');
  }

  switchLanguage(event: any) {
    const lang = event.detail.value;
    // Update the language service (this will notify all subscribers)
    this.languageService.switchLanguage(lang);
  }
}
